export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: {
    name: string;
    avatar?: string;
  };
  publishedAt: string;
  category: string;
  tags: string[];
  image: string;
  readTime: number; // in minutes
  featured?: boolean;
}

export const blogPosts: BlogPost[] = [
  {
    id: "1",
    slug: "10-essential-tips-for-solo-travelers",
    title: "10 Essential Tips for Solo Travelers",
    excerpt: "Discover the secrets to safe, enjoyable, and memorable solo travel adventures. From packing smart to staying connected, these tips will transform your journey.",
    content: `
      <p>Solo travel is one of the most rewarding experiences you can have. It pushes you out of your comfort zone, builds confidence, and creates unforgettable memories. However, traveling alone also comes with unique challenges. Here are 10 essential tips to make your solo adventure safe, enjoyable, and memorable.</p>
      
      <h2>1. Research Your Destination Thoroughly</h2>
      <p>Before you go, research your destination extensively. Understand the local culture, customs, and any safety concerns. Check travel advisories and read reviews from other solo travelers. Knowledge is your best friend when traveling alone.</p>
      
      <h2>2. Pack Light and Smart</h2>
      <p>When you're traveling solo, you're responsible for everything. Pack light so you can move easily and keep your belongings secure. Invest in a good backpack or suitcase with lockable zippers, and always keep important documents in a secure, easily accessible place.</p>
      
      <h2>3. Stay Connected</h2>
      <p>Share your itinerary with family or friends back home. Check in regularly, especially when moving between destinations. Consider getting a local SIM card or an international data plan to stay connected.</p>
      
      <h2>4. Trust Your Instincts</h2>
      <p>Your intuition is a powerful tool. If something doesn't feel right, trust that feeling. Don't be afraid to say no, walk away, or change your plans if you feel uncomfortable.</p>
      
      <h2>5. Meet Other Travelers</h2>
      <p>Stay in hostels, join group tours, or use apps to meet other travelers. Having a community, even temporarily, can enhance your experience and provide a safety net.</p>
      
      <h2>6. Keep Copies of Important Documents</h2>
      <p>Make digital copies of your passport, visa, travel insurance, and other important documents. Store them in cloud storage and keep physical copies in a separate location from the originals.</p>
      
      <h2>7. Learn Basic Local Phrases</h2>
      <p>Learning a few key phrases in the local language can go a long way. It shows respect for the culture and can help you navigate situations more easily.</p>
      
      <h2>8. Stay Aware of Your Surroundings</h2>
      <p>While it's important to enjoy yourself, always stay aware of your surroundings. Avoid walking alone at night in unfamiliar areas, and be cautious with alcohol consumption.</p>
      
      <h2>9. Have a Backup Plan</h2>
      <p>Things don't always go as planned. Have backup accommodation options, extra funds, and alternative transportation methods. Flexibility is key to successful solo travel.</p>
      
      <h2>10. Embrace the Experience</h2>
      <p>Finally, embrace every moment. Solo travel is about growth, discovery, and freedom. Don't be afraid to step out of your comfort zone and create the adventure you've always dreamed of.</p>
      
      <p>Remember, solo travel isn't about being alone—it's about discovering yourself while exploring the world. With these tips in mind, you're ready to embark on an incredible journey!</p>
    `,
    author: {
      name: "Sarah Johnson",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah"
    },
    publishedAt: "2025-01-05",
    category: "Travel Tips",
    tags: ["Solo Travel", "Tips", "Safety", "Planning"],
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop",
    readTime: 8,
    featured: true
  },
  {
    id: "2",
    slug: "best-destinations-for-first-time-solo-travelers",
    title: "Best Destinations for First-Time Solo Travelers",
    excerpt: "Planning your first solo trip? Discover the most welcoming and safe destinations that are perfect for beginners. From vibrant cities to serene beaches, find your perfect starting point.",
    content: `
      <p>Your first solo trip is a milestone moment. Choosing the right destination can make all the difference between a stressful experience and the adventure of a lifetime. Here are the best destinations for first-time solo travelers.</p>
      
      <h2>1. Manali, India</h2>
      <p>Manali offers a perfect blend of adventure and tranquility. With friendly locals, excellent infrastructure, and stunning mountain views, it's ideal for solo travelers. The town is safe, walkable, and full of hostels where you can meet other travelers.</p>
      
      <h2>2. Goa, India</h2>
      <p>Goa's laid-back atmosphere makes it perfect for solo travelers. Beautiful beaches, vibrant nightlife, and a strong traveler community create a welcoming environment. It's easy to navigate and offers plenty of activities for every interest.</p>
      
      <h2>3. Udaipur, India</h2>
      <p>Known as the "City of Lakes," Udaipur is safe, beautiful, and rich in culture. Solo travelers will appreciate the city's walkability, excellent food scene, and numerous opportunities to connect with locals and other travelers.</p>
      
      <h2>4. Ooty, India</h2>
      <p>Ooty's peaceful hill station atmosphere is perfect for those seeking a quieter solo travel experience. The town is safe, the people are friendly, and the natural beauty is breathtaking. It's an excellent choice for first-time solo travelers looking for tranquility.</p>
      
      <p>Each of these destinations offers unique experiences while being particularly welcoming to solo travelers. Choose based on your interests—whether you prefer mountains, beaches, culture, or adventure!</p>
    `,
    author: {
      name: "Rajesh Kumar",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rajesh"
    },
    publishedAt: "2025-01-03",
    category: "Destinations",
    tags: ["First Time", "Destinations", "India", "Beginners"],
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    readTime: 6,
    featured: false
  },
  {
    id: "3",
    slug: "how-to-pack-light-for-your-adventure",
    title: "How to Pack Light for Your Adventure",
    excerpt: "Master the art of minimalist packing. Learn how to fit everything you need into a single carry-on and travel lighter, faster, and more freely.",
    content: `
      <p>Packing light is a skill that transforms your travel experience. When you're not weighed down by luggage, you're more mobile, flexible, and free to explore. Here's how to pack like a pro.</p>
      
      <h2>The Golden Rule: Pack Half, Bring Double the Money</h2>
      <p>This old traveler's saying holds true. You'll need less than you think, and you can always buy what you need on the road.</p>
      
      <h2>Choose Versatile Clothing</h2>
      <p>Pack items that can be mixed and matched. Choose neutral colors that work together. A few well-chosen pieces can create multiple outfits.</p>
      
      <h2>Roll, Don't Fold</h2>
      <p>Rolling your clothes saves space and reduces wrinkles. It's more efficient than traditional folding and allows you to fit more in less space.</p>
      
      <h2>Use Packing Cubes</h2>
      <p>Packing cubes help organize your belongings and compress your clothes. They make it easy to find what you need without unpacking everything.</p>
      
      <h2>Limit Shoes</h2>
      <p>Shoes take up the most space. Limit yourself to 2-3 pairs maximum: one for walking, one for comfort, and optionally one for special occasions.</p>
      
      <h2>Go Digital</h2>
      <p>Instead of physical books and maps, use your phone or tablet. Download guides, maps, and entertainment before you go.</p>
      
      <p>Remember: packing light isn't about deprivation—it's about freedom. The less you carry, the more you can experience!</p>
    `,
    author: {
      name: "Priya Sharma",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Priya"
    },
    publishedAt: "2024-12-28",
    category: "Travel Tips",
    tags: ["Packing", "Tips", "Minimalism", "Preparation"],
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=800&h=600&fit=crop",
    readTime: 5,
    featured: false
  },
  {
    id: "4",
    slug: "making-friends-while-traveling-solo",
    title: "Making Friends While Traveling Solo",
    excerpt: "Solo travel doesn't mean traveling alone. Discover proven strategies for meeting amazing people and building connections on the road.",
    content: `
      <p>One of the biggest misconceptions about solo travel is that you'll be lonely. In reality, solo travel often leads to more meaningful connections than group travel. Here's how to make friends on the road.</p>
      
      <h2>Stay in Social Accommodations</h2>
      <p>Hostels, guesthouses, and co-living spaces are designed for social interaction. Common areas, shared kitchens, and organized activities make it easy to meet people.</p>
      
      <h2>Join Group Activities</h2>
      <p>Sign up for walking tours, cooking classes, or adventure activities. These shared experiences create natural conversation starters and bonding opportunities.</p>
      
      <h2>Use Social Apps</h2>
      <p>Apps like Meetup, Couchsurfing, and travel-specific social platforms can help you connect with locals and other travelers in your area.</p>
      
      <h2>Be Approachable</h2>
      <p>Smile, make eye contact, and be open to conversations. Sometimes a simple "hello" can lead to a great friendship or travel partnership.</p>
      
      <h2>Share Meals</h2>
      <p>Food brings people together. Invite someone to share a meal, or accept an invitation. Dining together is one of the best ways to connect.</p>
      
      <h2>Attend Local Events</h2>
      <p>Check out local festivals, markets, or community events. These gatherings are perfect for meeting both locals and travelers.</p>
      
      <p>Remember, everyone is in the same boat—looking for connection and adventure. Don't be afraid to make the first move!</p>
    `,
    author: {
      name: "Ananya Patel",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ananya"
    },
    publishedAt: "2024-12-25",
    category: "Stories",
    tags: ["Social", "Community", "Friendship", "Connection"],
    image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop",
    readTime: 6,
    featured: false
  },
  {
    id: "5",
    slug: "safety-tips-for-solo-female-travelers",
    title: "Safety Tips for Solo Female Travelers",
    excerpt: "Empowering advice for women traveling alone. Learn essential safety strategies that will give you confidence and peace of mind on your solo adventures.",
    content: `
      <p>Traveling solo as a woman is an empowering experience, but it requires extra awareness and preparation. Here are essential safety tips to help you travel confidently and safely.</p>
      
      <h2>Research Your Destination</h2>
      <p>Before traveling, research how women are treated in your destination. Some places are more conservative than others, and understanding local customs is crucial for your safety and comfort.</p>
      
      <h2>Dress Appropriately</h2>
      <p>Respect local dress codes, especially in conservative areas. This not only shows respect but can also help you blend in and avoid unwanted attention.</p>
      
      <h2>Trust Your Instincts</h2>
      <p>Your intuition is your best safety tool. If something feels off, trust that feeling. Don't worry about being polite if it means compromising your safety.</p>
      
      <h2>Stay Connected</h2>
      <p>Share your itinerary with trusted friends or family. Check in regularly, especially when moving between destinations. Consider using location-sharing apps.</p>
      
      <h2>Choose Accommodations Wisely</h2>
      <p>Read reviews carefully, especially from other solo female travelers. Choose places in safe neighborhoods with good security and positive reviews.</p>
      
      <h2>Be Cautious with Alcohol</h2>
      <p>If you choose to drink, do so responsibly. Never leave your drink unattended, and be aware of your limits. Consider having a buddy system even when traveling solo.</p>
      
      <h2>Have Emergency Contacts</h2>
      <p>Save local emergency numbers, embassy contacts, and trusted contacts in your phone. Keep a physical copy as backup.</p>
      
      <p>Remember: being cautious doesn't mean being fearful. With proper preparation, solo travel as a woman can be incredibly rewarding and empowering!</p>
    `,
    author: {
      name: "Meera Desai",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Meera"
    },
    publishedAt: "2024-12-20",
    category: "Travel Tips",
    tags: ["Safety", "Women", "Tips", "Empowerment"],
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop",
    readTime: 7,
    featured: false
  },
  {
    id: "6",
    slug: "budget-travel-hacks-for-solo-adventurers",
    title: "Budget Travel Hacks for Solo Adventurers",
    excerpt: "Travel more for less! Discover insider tips and tricks to stretch your travel budget without compromising on experiences.",
    content: `
      <p>Traveling solo doesn't have to break the bank. With the right strategies, you can explore the world on a budget while still having amazing experiences. Here are proven budget travel hacks.</p>
      
      <h2>Travel During Off-Season</h2>
      <p>Prices drop significantly during off-peak times. You'll save on flights, accommodation, and activities while enjoying fewer crowds.</p>
      
      <h2>Use Budget Accommodations</h2>
      <p>Hostels, guesthouses, and homestays offer great value. Many provide free breakfast, Wi-Fi, and social spaces where you can meet other travelers.</p>
      
      <h2>Cook Your Own Meals</h2>
      <p>Choose accommodations with kitchen access. Cooking even a few meals yourself can save significant money, and it's a great way to experience local markets.</p>
      
      <h2>Walk or Use Public Transport</h2>
      <p>Walking is free and the best way to explore. When you need to go further, public transportation is much cheaper than taxis or tours.</p>
      
      <h2>Look for Free Activities</h2>
      <p>Many cities offer free walking tours, free museum days, and free events. Research before you go to find these opportunities.</p>
      
      <h2>Travel Slowly</h2>
      <p>Staying longer in one place often means better deals on accommodation. Plus, you'll have more time to find local gems and avoid tourist traps.</p>
      
      <h2>Use Travel Rewards</h2>
      <p>Sign up for airline and hotel loyalty programs. Even if you don't travel frequently, points can add up and save you money.</p>
      
      <p>Remember: the best travel experiences often cost the least. Focus on authentic experiences over expensive tours!</p>
    `,
    author: {
      name: "Vikram Singh",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Vikram"
    },
    publishedAt: "2024-12-15",
    category: "Travel Tips",
    tags: ["Budget", "Money", "Tips", "Savings"],
    image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop",
    readTime: 6,
    featured: false
  },
  {
    id: "7",
    slug: "my-first-solo-trip-to-manali",
    title: "My First Solo Trip to Manali: A Journey of Self-Discovery",
    excerpt: "Join me as I share my transformative first solo travel experience in the beautiful mountains of Manali. From fears to freedom, this is my story.",
    content: `
      <p>I'll never forget the mix of excitement and terror I felt as I boarded the bus to Manali for my first solo trip. It was a decision that would change my life.</p>
      
      <h2>The Decision</h2>
      <p>After months of planning and second-guessing, I finally booked my ticket. The fear was real, but so was the desire for adventure and independence.</p>
      
      <h2>Arrival and First Impressions</h2>
      <p>Stepping off the bus in Manali, I was immediately struck by the crisp mountain air and the stunning views. The town was bustling but welcoming, and I felt a sense of freedom I'd never experienced before.</p>
      
      <h2>Meeting Fellow Travelers</h2>
      <p>At my hostel, I met travelers from all over the world. We shared stories, planned adventures together, and formed friendships that would last long after the trip ended.</p>
      
      <h2>The Challenges</h2>
      <p>It wasn't all smooth sailing. I got lost, struggled with the altitude, and had moments of loneliness. But each challenge taught me something about myself and my capabilities.</p>
      
      <h2>The Breakthrough</h2>
      <p>On day three, hiking alone to a viewpoint, I had a moment of clarity. I realized I was capable, independent, and stronger than I'd ever given myself credit for.</p>
      
      <h2>Lessons Learned</h2>
      <p>This trip taught me that solo travel isn't about being alone—it's about discovering who you are when no one is watching. It's about pushing boundaries, facing fears, and coming out stronger.</p>
      
      <p>If you're considering your first solo trip, I say: do it. The fear is temporary, but the growth and memories are forever.</p>
    `,
    author: {
      name: "Kavya Reddy",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Kavya"
    },
    publishedAt: "2024-12-10",
    category: "Stories",
    tags: ["Personal Story", "Manali", "First Time", "Inspiration"],
    image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
    readTime: 8,
    featured: false
  },
  {
    id: "8",
    slug: "sustainable-travel-practices-for-solo-travelers",
    title: "Sustainable Travel Practices for Solo Travelers",
    excerpt: "Travel responsibly and leave a positive impact. Learn how solo travelers can make a difference through sustainable practices and conscious choices.",
    content: `
      <p>As solo travelers, we have a unique opportunity to travel more sustainably. Without the constraints of group travel, we can make choices that benefit both us and the planet.</p>
      
      <h2>Choose Sustainable Transportation</h2>
      <p>Opt for trains, buses, or walking over flights when possible. When you must fly, consider carbon offset programs. Slow travel is not only more sustainable but often more rewarding.</p>
      
      <h2>Support Local Businesses</h2>
      <p>Eat at local restaurants, shop at local markets, and stay in locally-owned accommodations. Your money directly supports the community you're visiting.</p>
      
      <h2>Reduce Plastic Waste</h2>
      <p>Carry a reusable water bottle, coffee cup, and shopping bag. Many destinations have refill stations, and this simple change significantly reduces your environmental impact.</p>
      
      <h2>Respect Local Culture and Environment</h2>
      <p>Learn about local customs and environmental concerns. Follow guidelines for protected areas, and always leave places better than you found them.</p>
      
      <h2>Choose Eco-Friendly Accommodations</h2>
      <p>Look for accommodations with environmental certifications or sustainable practices. Many hostels and guesthouses are leading the way in eco-tourism.</p>
      
      <h2>Pack Responsibly</h2>
      <p>Bring eco-friendly toiletries, avoid single-use items, and pack light to reduce transportation emissions.</p>
      
      <p>Remember: every small action counts. As solo travelers, we can be ambassadors for sustainable travel and inspire others through our choices!</p>
    `,
    author: {
      name: "Arjun Mehta",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Arjun"
    },
    publishedAt: "2024-12-05",
    category: "Travel Tips",
    tags: ["Sustainability", "Environment", "Responsible Travel", "Eco-Friendly"],
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
    readTime: 6,
    featured: false
  }
];

export const categories = ["All", "Travel Tips", "Destinations", "Stories"];

export const getAllTags = (): string[] => {
  const tagSet = new Set<string>();
  blogPosts.forEach(post => {
    post.tags.forEach(tag => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
};

export const getBlogBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

export const getFeaturedPost = (): BlogPost | undefined => {
  return blogPosts.find(post => post.featured);
};

export const getRelatedPosts = (currentPost: BlogPost, limit: number = 3): BlogPost[] => {
  return blogPosts
    .filter(post => 
      post.id !== currentPost.id && 
      (post.category === currentPost.category || 
       post.tags.some(tag => currentPost.tags.includes(tag)))
    )
    .slice(0, limit);
};

